import {useLayoutEffect, useState} from 'react';

/** What to show before a measurement exists, or where ResizeObserver does not (tests, SSR). */
export const FALLBACK_CARD_COUNT = 3;

/**
 * How many fixed-width cards fit across the measured element, remeasured whenever it changes width.
 *
 * The strip renders exactly this many rather than overflowing, because a horizontally scrolling row of
 * suggestions buys a scrollbar and a half-visible card and gives nothing back: what is off-screen is the
 * panel's job. n cards occupy n * cardWidth + (n - 1) * gap, so the count that fits a width w is
 * floor((w + gap) / (cardWidth + gap)); at least one, so a narrow column still shows a suggestion.
 *
 * The element arrives through a callback ref held in state rather than a ref object, because the strip
 * renders nothing at all until its read comes back: a ref object would still be empty on the mount the
 * effect runs on, and the row would keep the fallback count forever.
 *
 * Shared by the product and bundle suggestion strips so the two rows measure and wrap identically.
 */
export const useFittedCardCount = (cardWidth: number, gap: number) => {
    const [row, setRow] = useState<HTMLElement | null>(null);
    const [count, setCount] = useState<number>(FALLBACK_CARD_COUNT);
    useLayoutEffect(() => {
        if (!row) return;
        const measure = () => {
            const width = row.clientWidth;
            // A zero width is a hidden or not-yet-laid-out row, not a row that fits nothing, so the last
            // real measurement is kept rather than collapsing the strip to one card.
            if (width <= 0) return;
            setCount(Math.max(1, Math.floor((width + gap) / (cardWidth + gap))));
        };
        measure();
        // Missing under jsdom and any non-browser render, where the fallback count is the whole answer.
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(row);
        return () => observer.disconnect();
    }, [row, cardWidth, gap]);
    return {rowRef: setRow, count};
};

export default useFittedCardCount;
